import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalExpiredDraftComponent } from '../../components/modal-expired-draft/modal-expired-draft.component';
import { AttachmentService } from './attachment.service';

const STORAGE_KEY_PREFIX = 'dia_rubrique_';
const STARTED_AT_KEY = 'dia_started_at';
const EXPIRATION_DAYS = 5;

@Injectable()
export class LocalStorageService {

  constructor(
    private readonly modalService: NgbModal
  ) {}

  /**
   * Sauvegarde les données d'une rubrique dans le localStorage.
   * Initialise la date de départ si elle n'existe pas encore.
   */
  save(rubrique: string, data: any): void {
    if (!localStorage.getItem(STARTED_AT_KEY)) {
      localStorage.setItem(STARTED_AT_KEY, String(Date.now()));
    }
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + rubrique, JSON.stringify(data));
    } catch (e) {
      console.warn('LocalStorageService: impossible de sauvegarder la rubrique', rubrique, e);
    }
  }

  /**
   * Charge les données d'une rubrique depuis le localStorage.
   * Retourne null si absent ou expiré.
   * Si les données sont expirées, affiche la modale d'expiration et vide le storage.
   */
  load(rubrique: string): any | null {
    this.checkAndClearIfExpired()

    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + rubrique);
    if (!raw) { return null; }
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * Vérifie si les données ont expiré (> EXPIRATION_DAYS jours depuis le premier enregistrement).
   */
  isExpired(): boolean {
    const startedAt = localStorage.getItem(STARTED_AT_KEY);
    if (!startedAt) { return false; }
    const expiresAt = parseInt(startedAt, 10) + EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() > expiresAt;
  }

  /**
   * Vérifie de façon proactive si les données ont expiré et les supprime le cas échéant.
   * À appeler au démarrage de l'application avant toute autre opération sur le localStorage.
   * @param attachmentService (optionnel) Si fourni, les pièces jointes seront également supprimées côté serveur.
   * @returns true si les données ont été supprimées, false sinon
   */
  checkAndClearIfExpired(attachmentService?: AttachmentService): boolean {
    if (this.isExpired()) {
      this.clearAll(attachmentService);
      this.modalService.open(ModalExpiredDraftComponent, { centered: true });
      return true;
    }
    return false;
  }

  /**
   * Supprime toutes les données du localStorage.
   * Si un AttachmentService est fourni, supprime également les pièces jointes côté serveur
   * (car les PJ sont uploadées immédiatement, contrairement au reste du formulaire).
   * Les cookies (sub, given_name, idCerfa, etc.) sont conservés car ils sont
   * définis à la connexion et ne font pas partie du brouillon.
   *
   * @param attachmentService (optionnel) Service de gestion des pièces jointes.
   */
  clearAll(attachmentService?: AttachmentService): void {
    // Supprimer les pièces jointes côté serveur avant de vider le localStorage
    if (attachmentService) {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX + 'idCerfa');
      if (raw) {
        try {
          const idCerfa = JSON.parse(raw);
          if (idCerfa) {
            attachmentService.deleteAllFiles(idCerfa).subscribe(
              () => {},
              (error) => console.warn('LocalStorageService: erreur suppression pièces jointes', error)
            );
          }
        } catch (e) {
          // idCerfa non parsable, impossible de supprimer les PJ
        }
      }
    }
    localStorage.clear();
  }

  /**
   * Utilitaire statique : restaure un FormArray depuis un tableau de valeurs sauvegardées.
   * Ajoute les lignes manquantes via createItemFn avant de patcher.
   */
  static restoreFormArray(formArray: FormArray, savedItems: any[], createItemFn: () => FormGroup): void {
    if (!savedItems || !Array.isArray(savedItems)) { return; }
    while (formArray.length < savedItems.length) {
      formArray.push(createItemFn());
    }
    formArray.patchValue(savedItems);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/local-storage.service.ts